import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  Paper,
} from "@mui/material";
import apiService from "../../../../../../service/Api";
import Pagination from "../../../../../../components/ui/Pagination";

const DEFAULT_PAGE_SIZE = 10;


export interface DonationResponse {
  id: number;
  reference: string;
  donor_name: string | null;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  is_anonymous: boolean;
  amount: string;
  currency: string;
  payment_method: string | null;
  paid: boolean;
  status?: "pending" | "paid" | "cancelled" | "failed" | string;
  created_at: string;
}

interface PaginatedDonations {
  results: DonationResponse[];
  count: number;
}

// A just-made donation isn't findable on a page it hasn't loaded yet — scan a
// bounded number of pages (ordered newest-first, so it's almost always on
// page 1) to locate it before giving up.
const MAX_HIGHLIGHT_SCAN_PAGES = 5;

interface DonationsTableProps {
  streamId?: number | string;
  campaignId?: number | string;
  title?: string;
  paid?: boolean;
  currency?: string;
  ordering?: string;
  refreshKey?: number;
  highlightDonationId?: number;
}

const DonationsTable: React.FC<DonationsTableProps> = ({
  streamId,
  campaignId,
  title = "All Donations",
  paid,
  currency,
  ordering = "-created_at",
  refreshKey = 0,
  highlightDonationId,
}) => {
  const [donations, setDonations] = useState<DonationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const highlightRowRef = useRef<HTMLTableRowElement | null>(null);
  const scannedForHighlightRef = useRef<number | undefined>(undefined);

  const buildParams = (pageNumber: number) => {
    // Prefer campaign scoping (native chema: catches donations with no stream);
    // fall back to stream scoping for legacy callers.
    const params: Record<string, string | number> = campaignId
      ? { campaign_id: campaignId }
      : { stream_id: streamId! };

    params.page = pageNumber + 1;
    params.page_size = pageSize;

    if (paid !== undefined) {
      params.paid = paid.toString();
    }
    if (currency) {
      params.currency = currency;
    }
    if (ordering) {
      params.ordering = ordering;
    }
    return params;
  };

  const fetchPage = async (pageNumber: number): Promise<PaginatedDonations> => {
    const response = await apiService.get<
      DonationResponse[] | { results: DonationResponse[]; count?: number }
    >("/api/chema/donations/", buildParams(pageNumber));

    // Handle both paginated and non-paginated responses
    const data = response.data;
    if (Array.isArray(data)) {
      return { results: data, count: data.length };
    }
    if (data && "results" in data) {
      return { results: data.results, count: data.count ?? data.results.length };
    }
    return { results: [], count: 0 };
  };

  // Reset to page 0 whenever the query changes or a refresh is requested.
  useEffect(() => {
    setPage(0);
    scannedForHighlightRef.current = undefined;
  }, [streamId, campaignId, paid, currency, ordering, refreshKey, pageSize]);

  useEffect(() => {
    if (!campaignId && !streamId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { results, count } = await fetchPage(page);
        if (cancelled) return;
        setDonations(results);
        setTotalCount(count);

        // If we're looking for a specific donation and it's not on this page,
        // keep scanning forward (bounded) — it'll typically be on page 0 since
        // the list is ordered newest-first.
        if (
          highlightDonationId &&
          scannedForHighlightRef.current !== highlightDonationId &&
          !results.some((d) => d.id === highlightDonationId) &&
          page < MAX_HIGHLIGHT_SCAN_PAGES - 1
        ) {
          setPage((p) => p + 1);
          return;
        }
        scannedForHighlightRef.current = highlightDonationId;
      } catch (err: unknown) {
        if (cancelled) return;
        console.error("Error fetching donations:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch donations";
        setError(errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, campaignId, paid, currency, ordering, refreshKey, page]);

  useEffect(() => {
    if (highlightDonationId && donations.some((d) => d.id === highlightDonationId)) {
      highlightRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [donations, highlightDonationId]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(-2);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}/${month}/${day}`;
    } catch {
      return dateString;
    }
  };

  type DonationStatus = "Paid" | "Pending" | "Cancelled" | "Failed";

  const getDonationStatus = (donation: DonationResponse): DonationStatus => {
    // Prefer the persisted status; fall back to the legacy paid flag for rows
    // that predate the status field.
    switch (donation.status) {
      case "paid":
        return "Paid";
      case "pending":
        return "Pending";
      case "cancelled":
        return "Cancelled";
      case "failed":
        return "Failed";
      default:
        return donation.paid ? "Paid" : "Pending";
    }
  };

  const STATUS_STYLES: Record<DonationStatus, { bg: string; color: string; dot: string }> = {
    Paid: { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
    Pending: { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
    Cancelled: { bg: "#fee2e2", color: "#991b1b", dot: "#9ca3af" },
    Failed: { bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  };

  const getStatusStyle = (status: DonationStatus) => STATUS_STYLES[status];

  const formatPaymentMethod = (method: string | null) => {
    if (!method) return "N/A";
    const methodMap: Record<string, string> = {
      ecocash: "EcoCash",
      onemoney: "OneMoney",
      innbucks: "InnBucks",
    };
    return methodMap[method.toLowerCase()] || method;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        {title}
      </Typography>

      {donations.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No donations found</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table
            sx={{
              minWidth: 640,
              "& td, & th": {
                borderBottom: "1px solid #e5e7eb",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Donor Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {donations.map((donation) => {
                const status = getDonationStatus(donation);
                const statusStyle = getStatusStyle(status);
                const isHighlighted = donation.id === highlightDonationId;
                return (
                  <TableRow
                    key={donation.id}
                    hover
                    ref={isHighlighted ? highlightRowRef : undefined}
                    sx={
                      isHighlighted
                        ? { bgcolor: "#fef3c7", transition: "background-color 1.5s ease" }
                        : undefined
                    }
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: statusStyle.dot,
                          }}
                        />
                        {formatDate(donation.created_at)}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 500 }}>
                      {donation.display_name || donation.donor_name || "Anonymous"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 500,
                          borderRadius: "12px",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {formatPaymentMethod(donation.payment_method)}
                    </TableCell>

                    <TableCell sx={{ fontWeight: "bold" }}>
                      {donation.currency === "ZWL" ? "ZWL" : "$"}
                      {parseFloat(donation.amount || "0").toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalCount > pageSize && (
        <Pagination
          currentPage={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </Paper>
  );
};

export default DonationsTable;